import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { Sparkle, CheckCircle, Crown } from "@phosphor-icons/react";
import PhotoUploader from "@/components/PhotoUploader";

const INPUT_CLS = "w-full bg-slate-900 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold";

const TIER_ICONS = {
    essential: null,
    signature: <Sparkle size={14} weight="fill" className="text-gold" />,
    butler_priority: <Crown size={14} weight="fill" className="text-gold" />,
};

const QUOTE_PREFILL_KEY = "butler_quote_prefill";

function loadPrefill() {
    try {
        const raw = localStorage.getItem(QUOTE_PREFILL_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

export default function NewTaskPage() {
    const [categories, setCategories] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [form, setForm] = useState(() => {
        const p = loadPrefill();
        return {
            title: "", description: "", category: "", address: "", preferred_date: "",
            photos: [], recurrence: "biweekly",
            square_feet: p?.square_feet || 2000,
            frequency: p?.frequency || "biweekly",
            service_tier: p?.service_tier || "signature",
            home_condition: p?.home_condition || "maintained",
            addons: p?.addons || {}, // {key: qty}
        };
    });
    const [quoteData, setQuoteData] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([http.get("/categories"), http.get("/pricing/catalog")]).then(([catRes, calRes]) => {
            setCategories(catRes.data.categories);
            setCatalog(calRes.data);
            setForm((f) => ({ ...f, category: catRes.data.categories[0] }));
        });
    }, []);

    // Auto-quote on any pricing input change (debounced)
    useEffect(() => {
        if (!catalog) return;
        const timer = setTimeout(async () => {
            setQuoting(true);
            try {
                const addons = Object.entries(form.addons)
                    .filter(([, qty]) => qty > 0)
                    .map(([key, qty]) => ({ key, qty }));
                const category_lower = (form.category || "").toLowerCase();
                const is_deep = category_lower.includes("move-in") || category_lower.includes("deep clean");
                const { data } = await http.post("/pricing/quote", {
                    square_feet: form.square_feet,
                    frequency: form.frequency,
                    service_tier: form.service_tier,
                    home_condition: form.home_condition,
                    service_type: is_deep ? "deep_clean" : "recurring",
                    addons,
                });
                setQuoteData(data);
            } catch (e) { /* silent — form-level errors handled on submit */ }
            finally { setQuoting(false); }
        }, 300);
        return () => clearTimeout(timer);
    }, [form.square_feet, form.frequency, form.service_tier, form.home_condition, form.addons, form.category, catalog]);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
    function setAddon(key, qty) {
        setForm((f) => ({ ...f, addons: { ...f.addons, [key]: qty } }));
    }

    async function submit(e) {
        e.preventDefault();
        setError(""); setSubmitting(true);
        try {
            const addons = Object.entries(form.addons)
                .filter(([, qty]) => qty > 0)
                .map(([key, qty]) => ({ key, qty }));
            // Map frequency to recurrence for legacy fields
            const recurrence_map = { weekly: "weekly", biweekly: "biweekly", monthly: "monthly", one_time: "none" };
            const payload = {
                title: form.title,
                description: form.description,
                category: form.category,
                address: form.address,
                preferred_date: form.preferred_date,
                photos: form.photos,
                recurrence: recurrence_map[form.frequency] || "none",
                square_feet: form.square_feet,
                frequency: form.frequency,
                service_tier: form.service_tier,
                home_condition: form.home_condition,
                addons,
            };
            const { data } = await http.post("/tasks", payload);
            navigate(`/tasks/${data.id}`);
        } catch (e) { setError(formatApiError(e)); }
        finally { setSubmitting(false); }
    }

    if (!catalog) return <div className="max-w-5xl mx-auto px-6 py-16 text-slate-500">Loading…</div>;

    return (
        <div data-testid="new-task-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
            <div className="label-eyebrow mb-3">/ Book a cleaning</div>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-10">Tell us about <span className="italic gold-text">your home</span>.</h1>

            <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7 space-y-6">
                    {/* Service basics */}
                    <Section title="Service">
                        <Field label="Task title">
                            <input
                                data-testid="task-title" required value={form.title} onChange={(e) => set("title", e.target.value)}
                                placeholder="e.g. Bi-weekly home cleaning"
                                className={INPUT_CLS}
                            />
                        </Field>
                        <Field label="Service type">
                            <select
                                data-testid="task-category" value={form.category} onChange={(e) => set("category", e.target.value)}
                                className={INPUT_CLS}
                            >
                                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Describe the home / any specifics">
                            <textarea
                                data-testid="task-description" required rows={4} value={form.description} onChange={(e) => set("description", e.target.value)}
                                placeholder="Floors, surfaces, pets, gates, access codes, anything we should know."
                                className={`${INPUT_CLS} resize-none`}
                            />
                        </Field>
                    </Section>

                    {/* Pricing inputs */}
                    <Section title="Pricing inputs">
                        <Field label={`Approximate home size: ${form.square_feet.toLocaleString()} sqft`}>
                            <input
                                data-testid="task-sqft" type="range" min={400} max={8000} step={100}
                                value={form.square_feet}
                                onChange={(e) => set("square_feet", parseInt(e.target.value, 10))}
                                className="w-full accent-[#D4AF37]"
                            />
                            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                                <span>400</span><span>2,000</span><span>4,000</span><span>6,000</span><span>8,000+</span>
                            </div>
                        </Field>

                        <Field label="How often?">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {catalog.frequencies.map((freq) => {
                                    const active = form.frequency === freq.key;
                                    return (
                                        <button
                                            type="button" key={freq.key}
                                            data-testid={`freq-${freq.key}`}
                                            onClick={() => set("frequency", freq.key)}
                                            className={`px-3 py-3 text-xs sm:text-sm rounded-sm border transition text-center ${
                                                active ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-slate-300 hover:border-white/25"
                                            }`}
                                        >
                                            <div className="font-medium">{freq.label}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>

                        <Field label="Home condition">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {catalog.conditions.map((c) => {
                                    const active = form.home_condition === c.key;
                                    return (
                                        <button
                                            type="button" key={c.key}
                                            data-testid={`cond-${c.key}`}
                                            onClick={() => set("home_condition", c.key)}
                                            className={`px-3 py-3 text-xs sm:text-sm rounded-sm border transition text-center ${
                                                active ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-slate-300 hover:border-white/25"
                                            }`}
                                        >
                                            <div className="font-medium">{c.key === "maintained" ? "Maintained" : c.label.split(" — ")[0]}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>

                        <Field label="Service tier">
                            <div className="space-y-2">
                                {catalog.tiers.map((tier) => {
                                    const active = form.service_tier === tier.key;
                                    const tierPrice = quoteData?.tier_ladder.find((t) => t.key === tier.key)?.final_price;
                                    const expanded = active || tier.is_default;
                                    return (
                                        <div
                                            key={tier.key}
                                            data-testid={`tier-${tier.key}-card`}
                                            className={`rounded-sm border transition ${active ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}
                                        >
                                            <button
                                                type="button"
                                                data-testid={`tier-${tier.key}`}
                                                onClick={() => set("service_tier", tier.key)}
                                                className="w-full px-4 py-4 text-left flex items-center justify-between gap-3"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        {TIER_ICONS[tier.key]}
                                                        <span className={`font-serif text-base sm:text-lg ${active ? "text-gold" : "text-white"}`}>
                                                            {tier.label}
                                                        </span>
                                                        {tier.is_default && !active && <span className="text-[9px] uppercase tracking-widest text-slate-500">Most popular</span>}
                                                    </div>
                                                    <div className="text-xs text-slate-400 mt-1">{tier.tagline}</div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    {tierPrice != null && (
                                                        <div className={`font-serif text-xl ${active ? "text-gold" : "text-slate-200"}`}>
                                                            ${tierPrice.toFixed(0)}
                                                        </div>
                                                    )}
                                                    <div className="text-[10px] uppercase tracking-widest text-slate-500">per visit</div>
                                                </div>
                                            </button>
                                            {expanded && tier.inclusions && (
                                                <div data-testid={`tier-${tier.key}-inclusions`} className="px-4 pb-4 pt-1 border-t border-white/5">
                                                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 mt-3">What&apos;s included</div>
                                                    <ul className="space-y-1.5">
                                                        {tier.inclusions.map((line, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-xs text-slate-300 leading-relaxed">
                                                                <CheckCircle size={12} weight="fill" className="text-gold mt-0.5 shrink-0" />
                                                                <span>{line}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Field>

                        <Field label="Add-ons (à la carte)">
                            <div className="space-y-2">
                                {catalog.addons.map((a) => {
                                    const qty = form.addons[a.key] || 0;
                                    const isLaundry = a.key === "laundry_folding";
                                    return (
                                        <div key={a.key} className="flex items-center justify-between gap-3 px-4 py-3 rounded-sm border border-white/10 bg-slate-900/40">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-slate-100">{a.label}</div>
                                                <div className="text-[11px] text-slate-500 mt-0.5">${a.rate.toFixed(0)}{isLaundry ? " / load" : ""}</div>
                                            </div>
                                            {isLaundry ? (
                                                <div className="flex items-center gap-2">
                                                    <button type="button" data-testid={`addon-${a.key}-dec`} onClick={() => setAddon(a.key, Math.max(0, qty - 1))}
                                                        className="w-7 h-7 rounded-sm border border-white/10 hover:border-gold/40 text-slate-300">−</button>
                                                    <span data-testid={`addon-${a.key}-qty`} className="w-6 text-center text-sm">{qty}</span>
                                                    <button type="button" data-testid={`addon-${a.key}-inc`} onClick={() => setAddon(a.key, qty + 1)}
                                                        className="w-7 h-7 rounded-sm border border-white/10 hover:border-gold/40 text-slate-300">+</button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    data-testid={`addon-${a.key}-toggle`}
                                                    onClick={() => setAddon(a.key, qty > 0 ? 0 : 1)}
                                                    className={`w-7 h-7 rounded-sm border transition ${
                                                        qty > 0 ? "border-gold bg-gold/15 text-gold" : "border-white/10 text-slate-500 hover:border-white/30"
                                                    }`}
                                                >{qty > 0 ? <CheckCircle size={16} weight="fill" /> : "+"}</button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Field>
                    </Section>

                    {/* Logistics */}
                    <Section title="Logistics">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Address">
                                <input
                                    data-testid="task-address" required value={form.address} onChange={(e) => set("address", e.target.value)}
                                    placeholder="123 Main St, Orlando, FL"
                                    className={INPUT_CLS}
                                />
                            </Field>
                            <Field label="Preferred date">
                                <input
                                    data-testid="task-date" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)}
                                    className={INPUT_CLS}
                                />
                            </Field>
                        </div>
                        <Field label="Photos (optional — helps us verify condition)">
                            <PhotoUploader value={form.photos} onChange={(photos) => setForm((f) => ({ ...f, photos }))} />
                        </Field>
                    </Section>

                    {error && <div data-testid="task-error" className="text-red-400 text-sm">{error}</div>}
                    <div className="flex gap-3 pt-2">
                        <button type="submit" data-testid="submit-task" disabled={submitting || !quoteData}
                            className="btn-gold disabled:opacity-50 w-full sm:w-auto">
                            {submitting ? "Submitting…" : "Submit & approve quote"}
                        </button>
                    </div>
                </div>

                {/* Sticky quote summary */}
                <aside className="lg:col-span-5">
                    <div data-testid="quote-summary" className="bg-slate-900/60 border border-white/10 p-6 rounded-sm lg:sticky lg:top-24">
                        <div className="label-eyebrow text-gold mb-3">/ Your quote</div>
                        {quoting && !quoteData ? (
                            <div className="text-slate-500 text-sm italic py-8 text-center">Calculating…</div>
                        ) : quoteData ? (
                            <>
                                <div className="flex items-end justify-between mb-1">
                                    <div className="text-xs text-slate-400 uppercase tracking-widest">{quoteData.quote.service_tier_label}</div>
                                    <div className="font-serif text-5xl gold-text leading-none">
                                        ${quoteData.quote.final_price.toFixed(0)}
                                    </div>
                                </div>
                                <div className="text-xs text-slate-500 text-right mb-5">per visit · ~{quoteData.quote.estimated_hours}h</div>

                                <div className="space-y-1.5 text-xs text-slate-400 border-t border-white/5 pt-4">
                                    {quoteData.quote.addons.length > 0 && (
                                        <>
                                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Add-ons</div>
                                            {quoteData.quote.addons.map((a) => (
                                                <LineItem key={a.key} label={`${a.label}${a.qty > 1 ? ` × ${a.qty}` : ""}`} value={`+$${a.total.toFixed(2)}`} />
                                            ))}
                                        </>
                                    )}
                                    {quoteData.quote.minimum_charge_applied && (
                                        <LineItem label="Minimum charge applied" value={`$${quoteData.quote.minimum_charge.toFixed(2)}`} accent />
                                    )}
                                </div>

                                <div className="mt-5 pt-4 border-t border-white/5 flex items-baseline justify-between">
                                    <div className="text-xs uppercase tracking-widest text-slate-400">Total per visit</div>
                                    <div className="font-serif text-2xl gold-text">${quoteData.quote.final_price.toFixed(2)}</div>
                                </div>

                                <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">
                                    Deterministic pricing — same inputs always produce the same number.
                                    A human Butler reviews every quote before final approval.
                                </p>
                            </>
                        ) : null}
                    </div>
                </aside>
            </form>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="space-y-5">
            <div className="label-eyebrow text-gold">/ {title}</div>
            <div className="space-y-5">{children}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function LineItem({ label, value, accent }) {
    return (
        <div className="flex justify-between gap-2">
            <span className={accent ? "text-gold" : "text-slate-400"}>{label}</span>
            <span className={accent ? "text-gold" : "text-slate-200"}>{value}</span>
        </div>
    );
}
